import { useState } from 'react';
import { useDatos } from '../../datos/DatosContext';
import { useAviso, useRetardo } from '../../lib/avisos';
import { formatearFecha, isoHace } from '../../lib/fechas';
import { borradorBiblioteca, ETIQUETAS_ACCION, type AccionBiblioteca, type BorradorBiblioteca, type Extension } from '../../datos/simulaciones';
import { Icono } from '../../componentes/ui/Icono';
import { BloqueIA, Bloqueado, Chip, Demo, GrupoChips, Pensando, TituloSeccion, Volver } from '../../componentes/ui/basicos';
import { NoEncontrado } from '../NoEncontrado';
import { estadoMaterial } from './Biblioteca';

type Paso = 'inactivo' | 'extension' | 'concepto' | 'cargando' | 'sinPermiso' | 'sinMaterial' | 'borrador';
const ACCIONES: AccionBiblioteca[] = ['resumen', 'ideas', 'capitulos', 'profundizar'];

export function ElementoBibliotecaDetalle({ id }: { id: string }) {
  const { datos, actualizar } = useDatos();
  const aviso = useAviso();
  const retardo = useRetardo();
  const [paso, setPaso] = useState<Paso>('inactivo');
  const [accion, setAccion] = useState<AccionBiblioteca | null>(null);
  const [extension, setExtension] = useState<Extension>('Breve');
  const [concepto, setConcepto] = useState('');
  const [borrador, setBorrador] = useState<BorradorBiblioteca | null>(null);
  const [editando, setEditando] = useState(false);

  const el = datos.biblioteca.find((b) => b.id === id);
  if (!el) return <NoEncontrado />;
  const material = estadoMaterial(el);

  const ejecutar = (a: AccionBiblioteca) => {
    setPaso('cargando');
    setEditando(false);
    retardo(() => { setBorrador(borradorBiblioteca(el, a, extension, concepto)); setPaso('borrador'); }, 1500);
  };

  const elegir = (a: AccionBiblioteca) => {
    setAccion(a);
    if (!el.permitirIA) { setPaso('sinPermiso'); return; }
    if (el.tipoMaterial === 'ninguno') { setPaso('sinMaterial'); return; }
    if (a === 'resumen') { setPaso('extension'); return; }
    if (a === 'profundizar') { setPaso('concepto'); return; }
    ejecutar(a);
  };

  const reiniciar = () => { setPaso('inactivo'); setAccion(null); setBorrador(null); setEditando(false); };

  const aprobar = () => {
    if (!borrador || !accion) return;
    const tipo = ETIQUETAS_ACCION[accion] + (accion === 'resumen' ? ' · ' + extension.toLowerCase() : '');
    actualizar('biblioteca', el.id, { aprobados: [{ tipo, fecha: formatearFecha(isoHace(0)), texto: borrador.texto }, ...el.aprobados] });
    reiniciar();
    aviso('Aprobado y guardado aparte del material original y de tus notas.');
  };

  return (
    <div className="stack-lg">
      <Volver a="/biblioteca">Biblioteca</Volver>
      <header>
        <p className="eyebrow">{el.tipo} · {el.estado}</p>
        <h1 className="display">{el.titulo}</h1>
        <p className="muted" style={{ margin: '6px 0 0' }}>{el.autor}</p>
      </header>

      <div className="grid2">
        <section className="card stack-sm">
          <h2 className="eyebrow">Material original</h2>
          <p style={{ margin: 0, fontWeight: 600 }}>{el.descripcionMaterial}</p>
          <span className={material.clase}>{material.texto}</span>
          <p className="small muted" style={{ margin: 0 }}>La IA solo trabaja con material realmente disponible. Un título o un enlace no bastan.</p>
        </section>
        <section className="card stack-sm">
          <h2 className="eyebrow">Mis notas personales</h2>
          <p className="prose" style={{ margin: 0 }}>{el.notasPersonales}</p>
        </section>
      </div>

      <section className="stack">
        <div className="between"><TituloSeccion>Trabajar con la IA</TituloSeccion><Demo>Respuestas simuladas</Demo></div>
        <div className="chips" role="group" aria-label="Acciones de IA">
          {ACCIONES.map((a) => <Chip key={a} activo={accion === a} onClick={() => elegir(a)}>{ETIQUETAS_ACCION[a]}</Chip>)}
        </div>

        {paso === 'extension' ? (
          <div className="card soft stack-sm">
            <span className="lbl">¿Qué extensión prefieres?</span>
            <GrupoChips etiqueta="Extensión" opciones={['Breve', 'Extenso']} seleccion={extension} onElegir={(v) => setExtension(v as Extension)} />
            <div><button className="btn primary" onClick={() => ejecutar('resumen')}><Icono nombre="spark" />Crear resumen</button></div>
          </div>
        ) : null}

        {paso === 'concepto' ? (
          <div className="card soft stack-sm">
            <label className="lbl" htmlFor="concept">¿En qué concepto quieres profundizar?</label>
            <input id="concept" type="text" placeholder="Por ejemplo: la pausa" value={concepto} onChange={(e) => setConcepto(e.target.value)} />
            <div><button className="btn primary" onClick={() => ejecutar('profundizar')}>Profundizar</button></div>
          </div>
        ) : null}

        {paso === 'cargando' ? <Pensando>Leyendo solo el material disponible…</Pensando> : null}

        {paso === 'sinPermiso' ? (
          <Bloqueado titulo="Este elemento no tiene permiso de consulta IA.">No se envía nada a la IA. Puedes activarlo en Notion o aquí más adelante.</Bloqueado>
        ) : null}

        {paso === 'sinMaterial' ? (
          <div className="ai-block falta stack-sm">
            <span className="eyebrow">Me faltan datos</span>
            <p style={{ margin: 0 }}>No tengo acceso al contenido de «{el.titulo}»: solo tengo el título y un enlace. Para resumirlo necesito una transcripción, un archivo o tus apuntes. No voy a inventar su contenido.</p>
            <div><button className="btn" onClick={() => aviso('Solo visual: aquí podrás pegar una transcripción o subir un archivo.')}>Añadir material <Demo /></button></div>
          </div>
        ) : null}

        {paso === 'borrador' && borrador && accion ? (
          <div className="draft stack-sm">
            <div className="between">
              <span className="tag dusk">Borrador de IA · {ETIQUETAS_ACCION[accion]} · no guardado</span>
              <span className="muted small">Modelo económico · 4 de 200 consultas este mes (ejemplo)</span>
            </div>
            {editando ? (
              <>
                <label className="sr" htmlFor="sum-edit">Editar borrador</label>
                <textarea id="sum-edit" rows={8} value={borrador.texto} onChange={(e) => setBorrador({ ...borrador, texto: e.target.value })} />
              </>
            ) : (
              <p className="prose" style={{ margin: '4px 0 0' }}>{borrador.texto}</p>
            )}
            <BloqueIA tipo="cita" etiqueta="Fuentes usadas" pequeno>{borrador.fuentes}</BloqueIA>
            {borrador.incierto ? <BloqueIA tipo="falta" etiqueta="Incierto o no disponible" pequeno>{borrador.incierto}</BloqueIA> : null}
            <div className="row">
              <button className="btn primary" onClick={aprobar}><Icono nombre="check" tam="sm" />Aprobar y guardar</button>
              <button className="btn" onClick={() => setEditando((v) => !v)}>{editando ? 'Ver borrador' : 'Editar antes de guardar'}</button>
              <button className="btn quiet" onClick={() => { reiniciar(); aviso('Borrador descartado. No se ha guardado nada.'); }}>Descartar</button>
            </div>
          </div>
        ) : null}
      </section>

      <section className="stack-sm">
        <TituloSeccion>Aprobado por ti</TituloSeccion>
        {el.aprobados.map((ap, i) => (
          <div key={i} className="card stack-sm">
            <div className="between"><span className="tag moss">{ap.tipo}</span><span className="muted small">Aprobado el {ap.fecha}</span></div>
            <p className="prose" style={{ margin: 0 }}>{ap.texto}</p>
          </div>
        ))}
        {!el.aprobados.length ? <p className="muted" style={{ margin: 0 }}>Todavía no has aprobado ningún resumen. Los borradores no se guardan hasta que los apruebes.</p> : null}
      </section>
    </div>
  );
}
